import { AcceptPendingInviteAction, RejectPendingInviteAction } from './pending-invites.actions';
import { Action, State, StateContext } from '@ngxs/store';
import { GroupsService, PendingUserOfGroup } from '@smatchy/sdk';
import { Injectable } from '@angular/core';
import { LoadingAdapter } from '../../../../adapters/loading.adapter';
import { catchError, switchMap, from, of } from 'rxjs';
import { RemovePendingUserFromListAction } from './group-content.actions';
import { TranslateService } from '@ngx-translate/core';

export type PendingInvitesStateModel = {
  /** null until a group's pending invites are loaded, which the defaults set. */
  groupId: string | null;
  pendingInvites: PendingUserOfGroup[];
};

const defaults: PendingInvitesStateModel = {
  groupId: null,
  pendingInvites: [],
};

@State<PendingInvitesStateModel>({
  name: 'pendingInvites',
  defaults,
})
@Injectable()
export class PendingInvitesState {
  private readonly errorMessageMap: Record<string, string> = {
    group_already_joined: 'PENDINGS_REQUESTS_TO_JOIN_GROUP_PAGE.USER_ALREADY_MEMBER',
    group_not_found: 'PENDINGS_REQUESTS_TO_JOIN_GROUP_PAGE.GROUP_NOT_FOUND',
  };

  constructor(
    private readonly _loadingAdapter: LoadingAdapter,
    private readonly _groupService: GroupsService,
    private readonly _translate: TranslateService,
  ) {}

  private handleErrorWithMessage(
    error: string,
    userId: string,
    dispatch: StateContext<PendingInvitesStateModel>['dispatch'],
  ) {
    const translationKey = this.errorMessageMap[error];
    if (translationKey) {
      const message = this._translate.instant(translationKey);
      return from(this._loadingAdapter.showMessage(message)).pipe(
        switchMap(() => {
          dispatch(new RemovePendingUserFromListAction(userId));
          return of(null);
        }),
      );
    }
    this._loadingAdapter.defaultError();
    return of(null);
  }

  private handleSuccess(userId: string, dispatch: StateContext<PendingInvitesStateModel>['dispatch']) {
    dispatch(new RemovePendingUserFromListAction(userId));
    return of(null);
  }

  @Action(AcceptPendingInviteAction)
  acceptPendingInvite(
    { dispatch }: StateContext<PendingInvitesStateModel>,
    { userId, groupId }: AcceptPendingInviteAction,
  ) {
    return this._groupService.acceptUserToJoinGroup({ userId, id: groupId }).pipe(
      switchMap((response) => {
        if (typeof response.value === 'string') {
          return this.handleErrorWithMessage(response.value, userId, dispatch);
        }
        if (response.value?.added) {
          return this.handleSuccess(userId, dispatch);
        }
        this._loadingAdapter.defaultError();
        return of(null);
      }),
      catchError(() => {
        this._loadingAdapter.defaultError();
        return of(null);
      }),
    );
  }

  @Action(RejectPendingInviteAction)
  rejectPendingInvite(
    { dispatch }: StateContext<PendingInvitesStateModel>,
    { userId, groupId }: RejectPendingInviteAction,
  ) {
    return this._groupService.rejectUserToJoinGroup({ userId, id: groupId }).pipe(
      switchMap((response) => {
        if (typeof response.value === 'string') {
          return this.handleErrorWithMessage(response.value, userId, dispatch);
        }
        if (response.value?.removed) {
          return this.handleSuccess(userId, dispatch);
        }
        this._loadingAdapter.defaultError();
        return of(null);
      }),
      catchError(() => {
        this._loadingAdapter.defaultError();
        return of(null);
      }),
    );
  }
}
